import type {SupabaseClient} from './supabaseTypes.js';

const MAX_MSG_LEN = 200;
const MAX_STACK_LEN = 500;

// Build-time deployed commit identifier (see vite.config.js). Guarded because
// Vitest does not apply Vite's `define`, so the bare identifier would throw a
// ReferenceError under `npm test`.
const APP_RELEASE = typeof __APP_RELEASE__ !== 'undefined' ? __APP_RELEASE__ : null;

const REDACT_PATTERNS = [
  /eyJ[A-Za-z0-9_-]{10,}/g, // JWT / base64 tokens
  /Bearer\s+\S+/gi,
  /supabase\.co\S*/gi,
  /access_token=[^\s&]+/gi,
  /refresh_token=[^\s&]+/gi,
  /apikey=[^\s&]+/gi,
  /password[=:]\s*\S+/gi,
  /localStorage\.\S+/gi,
];

// `typeof x === 'object'` alone narrows `unknown` only to `object`, which has no
// readable properties. This is the one narrowing the thrown-value reads below
// need, written as a predicate so no call site casts.
function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// Accepts `unknown` because that is what it is handed: a stack string, a
// message, or (as the `!str || typeof str !== 'string'` guard has always
// admitted) anything at all off a thrown value. That guard is the reason the
// return type is unconditionally `string`.
//
// ASCII ONLY in this file: error_resilience_static.test.js asserts every byte of
// this module is ASCII, so comments here use plain punctuation.
export function redactString(str: unknown): string {
  if (!str || typeof str !== 'string') return '';
  let out = str;
  for (const pat of REDACT_PATTERNS) {
    out = out.replace(pat, '[REDACTED]');
  }
  return out;
}

// The payload record_client_error receives, one field per RPC parameter, plus
// whatever `extra` the caller merged on top.
//
// `error_kind` is `unknown`, not `string`, and that is deliberate rather than
// lazy: it is `(error && error.name) || 'Error'` on a value that may be an
// Error, a string, a DOM ErrorEvent's `.error`, or a thrown object. `message` and
// `stack_summary` ARE `string` because redactString() returns '' for anything
// that is not one, so those two are already normalized before they land here.
export type ClientErrorEvent = {
  source: string;
  error_kind: unknown;
  message: string;
  stack_summary: string;
  route: string | null;
  user_agent: string | null;
  timestamp: string;
  [field: string]: unknown;
};

export function buildErrorEvent(source: string, error: unknown, extra?: Record<string, unknown> | null) {
  const name = (isObjectLike(error) && error.name) || 'Error';
  const rawMsg = (isObjectLike(error) && error.message) || String(error || '');
  // `.stack` is narrowed to a string where `.name`/`.message` are not, because
  // this is the ONE field the module calls a string method on directly
  // (`.split`) instead of routing through redactString(), which already returns
  // '' for a non-string. The narrowing states the assumption the split has
  // always made, one step earlier, rather than casting it away.
  const rawStack = (isObjectLike(error) && typeof error.stack === 'string' && error.stack) || '';

  const message = redactString(rawMsg).slice(0, MAX_MSG_LEN);
  const stackSummary = redactString(rawStack.split('\n').slice(0, 5).join('\n')).slice(0, MAX_STACK_LEN);

  const event: ClientErrorEvent = {
    source,
    error_kind: name,
    message,
    stack_summary: stackSummary,
    route: typeof window !== 'undefined' ? window.location.pathname : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    timestamp: new Date().toISOString(),
    ...(extra || {}),
  };
  return event;
}

let _sb: SupabaseClient | null = null;
let _reporting = false;

export function initErrorReporting(sb: SupabaseClient): void {
  _sb = sb;
}

export async function reportError(source: string, error: unknown, extra?: Record<string, unknown> | null) {
  const evt = buildErrorEvent(source, error, extra);

  if (typeof console !== 'undefined' && console.warn) {
    console.warn(`[WCF Error] ${evt.source}: ${evt.error_kind} - ${evt.message}`);
  }

  if (!_sb || _reporting) return;
  _reporting = true;
  try {
    await _sb.rpc('record_client_error', {
      p_source: evt.source,
      p_error_kind: evt.error_kind,
      p_message: evt.message,
      p_stack_summary: evt.stack_summary,
      p_route: evt.route,
      p_user_agent: evt.user_agent,
      p_app_version: APP_RELEASE,
    });
  } catch (_e) {
    // best-effort -- never let error reporting break the app
  } finally {
    _reporting = false;
  }
}

let _listenersInstalled = false;
export function installGlobalListeners(): void {
  if (typeof window === 'undefined' || _listenersInstalled) return;
  _listenersInstalled = true;

  window.addEventListener('error', (event) => {
    reportError('window.error', event.error || event.message);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError(
      'unhandledrejection',
      reason instanceof Error ? reason : new Error(String(reason || 'Unknown rejection')),
    );
  });
}
